<script setup lang="ts">
/**
 * "A new version is available — reload." (SPEC §13.1, MT-112)
 *
 * Non-blocking, in every sense the specification means. It never reloads on
 * its own and never on a timer; it does not cover the screen; and it can be
 * put away. Somebody half-way through saying how many of them are coming
 * finishes that first — the RSVP card holds this back while it is being edited
 * (see `holdUpdatePrompt` in the app store), and nothing here can take the page
 * out from under them.
 *
 * Dismissing it lasts for this visit only. The update is still waiting and
 * still applies the next time the application is opened; being asked again
 * then is being told something true, not being nagged.
 */
import { computed, ref } from 'vue'
import { useAppStore } from '@/stores/app'
import { applyUpdate } from '@/pwa'

const props = withDefaults(
  defineProps<{
    /** The reload. A prop so a test can observe it without reloading jsdom. */
    apply?: () => Promise<void> | void
  }>(),
  { apply: applyUpdate },
)

const app = useAppStore()

const dismissed = ref(false)
const applying = ref(false)

const visible = computed(() => app.updateAvailable && !dismissed.value)

async function reload(): Promise<void> {
  applying.value = true
  try {
    await props.apply()
  } finally {
    // Normally unreachable: a successful apply reloads the page. If it did not
    // — the worker vanished between prompt and press — the button comes back
    // rather than spinning for ever.
    applying.value = false
  }
}
</script>

<template>
  <v-snackbar
    :model-value="visible"
    :timeout="-1"
    location="bottom"
    color="surface"
    elevation="3"
    multi-line
    data-testid="update-prompt"
    @update:model-value="dismissed = true"
  >
    A new version is available.

    <template #actions>
      <v-btn variant="text" data-testid="update-later" @click="dismissed = true">Later</v-btn>
      <v-btn
        color="primary"
        variant="flat"
        :loading="applying"
        data-testid="update-reload"
        @click="reload"
      >
        Reload
      </v-btn>
    </template>
  </v-snackbar>
</template>
